import React from 'react';
import { IconLightbulb, IconInfoCircle } from './OnboardingIcons';

const AssetPatternsForm = ({ data, errors, onUpdate }) => {
  const handlePatternChange = (field, value) => {
    onUpdate('asset_id_pattern', {
      ...data.asset_id_pattern,
      [field]: value
    });
  };

  const handleSerialPatternChange = (field, value) => {
    onUpdate('asset_id_pattern', {
      ...data.asset_id_pattern,
      serial_number_pattern: {
        ...data.asset_id_pattern.serial_number_pattern,
        [field]: value
      }
    });
  };

  return (
    <div className="form-section">
      <div className="section-header">
        <h3>
          Asset ID Patterns
        </h3>
        {/* Named by what it does, not by the JSON key it writes to. The user is
            registering a company, not editing descriptors.json. */}
        <p>
          Describe how your asset IDs are shaped so a search for one can be
          traced back to you. Every field is optional.
        </p>
      </div>

      {/* The section heading said "Asset ID Patterns" directly under a page
          title that said the same thing, so this group is titled by what
          distinguishes it from the serial-number group below. */}
      <div className="pattern-section">
        <h4>
          Asset IDs
        </h4>
        <p className="pattern-description">
          Give an example, or the parts your IDs have in common.
        </p>

        <div className="form-grid">
          <div className="form-group full-width">
            <label htmlFor="global_asset_id" className="form-label">
              Global Asset ID (Example)
            </label>
            <input
              type="text"
              id="global_asset_id"
              className="form-input"
              value={data.asset_id_pattern.global_asset_id}
              onChange={(e) => handlePatternChange('global_asset_id', e.target.value)}
              placeholder="e.g., https://admin-shell.io/idta/asset/example-001"
            />
            <small className="form-help-text">
              An example of a complete asset ID used by your data provider
            </small>
          </div>

          <div className="form-group full-width">
            <label htmlFor="asset_id_starts_with" className="form-label">
              Asset ID Starts With
            </label>
            <input
              type="text"
              id="asset_id_starts_with"
              className="form-input"
              value={data.asset_id_pattern.asset_id_starts_with}
              onChange={(e) => handlePatternChange('asset_id_starts_with', e.target.value)}
              placeholder="e.g., https://admin-shell.io/idta"
            />
            <small className="form-help-text">
              The prefix that all your asset IDs start with
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="asset_id_contains" className="form-label">
              Asset ID Contains
            </label>
            <input
              type="text"
              id="asset_id_contains"
              className="form-input"
              value={data.asset_id_pattern.asset_id_contains}
              onChange={(e) => handlePatternChange('asset_id_contains', e.target.value)}
              placeholder="e.g., admin-shell.io"
            />
            <small className="form-help-text">
              A substring that appears in all your asset IDs
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="asset_id_ends_with" className="form-label">
              Asset ID Ends With
            </label>
            <input
              type="text"
              id="asset_id_ends_with"
              className="form-input"
              value={data.asset_id_pattern.asset_id_ends_with}
              onChange={(e) => handlePatternChange('asset_id_ends_with', e.target.value)}
              placeholder="e.g., /asset"
            />
            <small className="form-help-text">
              The suffix that all your asset IDs end with
            </small>
          </div>
        </div>
      </div>

      {/* Serial Number Patterns */}
      <div className="pattern-section">
        <h4>
          Serial Numbers
        </h4>
        <p className="pattern-description">
          Optional. Add these if your assets are also findable by serial number.
        </p>

        <div className="form-grid">
          <div className="form-group full-width">
            <label htmlFor="serial_number_format" className="form-label">
              Serial Number Format
            </label>
            <input
              type="text"
              id="serial_number_format"
              className="form-input"
              value={data.asset_id_pattern.serial_number_pattern.serial_number_format}
              onChange={(e) => handleSerialPatternChange('serial_number_format', e.target.value)}
              placeholder="e.g., IDTA-XXXXX or SN-YYYY-XXXXX"
            />
            <small className="form-help-text">
              Describe the format of your serial numbers (use X for alphanumeric, Y for numeric)
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="serial_number_starts_with" className="form-label">
              Serial Number Starts With
            </label>
            <input
              type="text"
              id="serial_number_starts_with"
              className="form-input"
              value={data.asset_id_pattern.serial_number_pattern.serial_number_starts_with}
              onChange={(e) => handleSerialPatternChange('serial_number_starts_with', e.target.value)}
              placeholder="e.g., IDTA-, SN-"
            />
            <small className="form-help-text">
              The prefix that all your serial numbers start with
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="serial_number_contains" className="form-label">
              Serial Number Contains
            </label>
            <input
              type="text"
              id="serial_number_contains"
              className="form-input"
              value={data.asset_id_pattern.serial_number_pattern.serial_number_contains}
              onChange={(e) => handleSerialPatternChange('serial_number_contains', e.target.value)}
              placeholder="e.g., IDTA, SIEMENS"
            />
            <small className="form-help-text">
              A substring that appears in all your serial numbers
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="serial_number_ends_with" className="form-label">
              Serial Number Ends With
            </label>
            <input
              type="text"
              id="serial_number_ends_with"
              className="form-input"
              value={data.asset_id_pattern.serial_number_pattern.serial_number_ends_with}
              onChange={(e) => handleSerialPatternChange('serial_number_ends_with', e.target.value)}
              placeholder="e.g., -END"
            />
            <small className="form-help-text">
              The suffix that all your serial numbers end with
            </small>
          </div>
        </div>
      </div>

      <div className="form-help">
        <h4>
          <IconLightbulb aria-hidden />
          Pattern Examples:
        </h4>
        <div className="pattern-examples">
          <div className="pattern-example-card">
            <div className="pattern-example-title">
              <strong>Asset ID Example (IDTA):</strong>
            </div>
            <div className="pattern-example-content">
              <div className="pattern-example-item">
                <span className="label">Starts With:</span>
                <code>https://admin-shell.io/idta</code>
              </div>
              <div className="pattern-example-item">
                <span className="label">Contains:</span>
                <code>admin-shell.io</code>
              </div>
              <div className="pattern-example-item">
                <span className="label">Example:</span>
                <code>https://admin-shell.io/idta/asset/example-001</code>
              </div>
            </div>
          </div>

          <div className="pattern-example-card">
            <div className="pattern-example-title">
              <strong>Serial Number Example (Siemens):</strong>
            </div>
            <div className="pattern-example-content">
              <div className="pattern-example-item">
                <span className="label">Format:</span>
                <code>SN-YYYY-SIEMENS-XXXX</code>
              </div>
              <div className="pattern-example-item">
                <span className="label">Starts With:</span>
                <code>SN-</code>
              </div>
              <div className="pattern-example-item">
                <span className="label">Contains:</span>
                <code>SIEMENS</code>
              </div>
              <div className="pattern-example-item">
                <span className="label">Example:</span>
                <code>SN-2024-SIEMENS-A123</code>
              </div>
            </div>
          </div>
        </div>

        <div className="alert alert-info mt-3">
          <IconInfoCircle aria-hidden />
          <strong>Note:</strong> These patterns enable smart asset discovery. The more specific your patterns, the better the matching accuracy.
        </div>
      </div>
    </div>
  );
};

export default AssetPatternsForm;
